import { useCallback, useEffect, useState, type CSSProperties, type ReactNode } from 'react'
import {
  AppstoreOutlined,
  BookOutlined,
  FileTextOutlined,
  ProjectOutlined,
  QuestionCircleOutlined,
  TeamOutlined,
} from '@ant-design/icons'
import { Modal, Spin, Typography, message } from 'antd'
import { adminApi, formatApiError } from '../api'
import type { DocTemplateFormat, DocTemplateItem } from '../types/docTemplate'
import './DocTemplatePicker.css'

const { Text } = Typography

const INLINE_TEMPLATE_LIMIT = 4

const TEMPLATE_ICONS: Record<string, ReactNode> = {
  outline: <FileTextOutlined />,
  meeting: <TeamOutlined />,
  manual: <BookOutlined />,
  project: <ProjectOutlined />,
  faq: <QuestionCircleOutlined />,
}

const TEMPLATE_ICON_TONES: Record<string, CSSProperties> = {
  outline: { background: '#e8f3ff', color: '#3370ff' },
  meeting: { background: '#e8f7ed', color: '#34c724' },
  manual: { background: '#fff3e8', color: '#ff8800' },
  project: { background: '#f3ebff', color: '#7b61ff' },
  faq: { background: '#e8f7f7', color: '#14c0c0' },
}

const DEFAULT_ICON_TONE: CSSProperties = { background: '#e8f3ff', color: '#3370ff' }

type ActiveTemplate = Pick<DocTemplateItem, 'id' | 'template_code' | 'title' | 'description' | 'content'>

type Props = {
  format: DocTemplateFormat
  onSelect: (content: string) => void
}

function TemplateCard({
  item,
  onClick,
  className,
}: {
  item: ActiveTemplate
  onClick: () => void
  className?: string
}) {
  const code = item.template_code || ''
  const iconTone = TEMPLATE_ICON_TONES[code] || DEFAULT_ICON_TONE
  return (
    <button type="button" className={className || 'doc-template-card'} onClick={onClick}>
      <span className="doc-template-card-icon" style={iconTone}>
        {(code && TEMPLATE_ICONS[code]) || <FileTextOutlined />}
      </span>
      <span className="doc-template-card-body">
        <span className="doc-template-card-title">{item.title}</span>
        <span className="doc-template-card-desc">{item.description}</span>
      </span>
    </button>
  )
}

export function DocTemplatePicker({ format, onSelect }: Props) {
  const [loading, setLoading] = useState(true)
  const [items, setItems] = useState<ActiveTemplate[]>([])
  const [modalOpen, setModalOpen] = useState(false)

  const load = useCallback(() => {
    setLoading(true)
    adminApi
      .get<{ items: ActiveTemplate[] }>('/admin/doc-templates/active', { params: { doc_format: format } })
      .then((r) => setItems(r.data.items || []))
      .catch((e) => {
        setItems([])
        message.error(formatApiError(e, '加载模板失败'))
      })
      .finally(() => setLoading(false))
  }, [format])

  useEffect(() => {
    load()
  }, [load])

  const handleSelect = useCallback(
    (content: string) => {
      onSelect(content)
      setModalOpen(false)
    },
    [onSelect],
  )

  if (loading) {
    return (
      <div className="doc-template-picker doc-template-picker--loading">
        <Spin size="small" />
        <Text type="secondary">加载模板…</Text>
      </div>
    )
  }

  if (!items.length) return null

  const inlineItems = items.slice(0, INLINE_TEMPLATE_LIMIT)

  return (
    <>
      <div className="doc-template-picker" role="region" aria-label="文档模板">
        <div className="doc-template-picker-head">
          <span className="doc-template-picker-title">选择模板快速开始</span>
        </div>
        <div className="doc-template-picker-grid">
          {inlineItems.map((item) => (
            <TemplateCard key={item.id} item={item} onClick={() => handleSelect(item.content)} />
          ))}
          <button
            type="button"
            className="doc-template-card doc-template-card--more"
            onClick={() => setModalOpen(true)}
          >
            <span className="doc-template-card-icon doc-template-card-icon--more">
              <AppstoreOutlined />
            </span>
            <span className="doc-template-card-body">
              <span className="doc-template-card-title">更多模板</span>
              <span className="doc-template-card-desc">查看全部 {items.length} 个</span>
            </span>
          </button>
        </div>
      </div>

      <Modal
        title="选择文档模板"
        open={modalOpen}
        onCancel={() => setModalOpen(false)}
        footer={null}
        width={720}
        destroyOnHidden
        className="doc-template-modal"
      >
        <div className="doc-template-modal-grid">
          {items.map((item) => (
            <TemplateCard
              key={item.id}
              item={item}
              className="doc-template-card doc-template-card--modal"
              onClick={() => handleSelect(item.content)}
            />
          ))}
        </div>
      </Modal>
    </>
  )
}
